import React, { useEffect, useState } from "react";
import MasterLayout from "../masterLayout/MasterLayout";
import Breadcrumb from "../components/Breadcrumb";
import InvoiceListLayer from "../components/InvoiceListLayer";
import DashboardFilter from "../components/DashboardFilter";
import { API_URL } from "..";
import { Spinner } from "react-bootstrap";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Icon } from "@iconify/react";

const DEFAULT_SUMMARY = {
  totalInvoicesCount: 0,
  totalInvoiceAmountCount: 0,
  paidInvoiceCount: 0,
  paidInvoiceAmountCount: 0,
  overdueInvoiceCount: 0,
  overdueInvoiceAmountCount: 0,
  unpaidInvoiceAmountCount: 0,
  unpaidInvoiceCount: 0,
};

const DEFAULT_PAGINATION = {
  page: 1,
  limit: 25,
  total: 0,
  totalPages: 1,
};

const InvoiceListPage = () => {
  const { status } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const selectedStatus = status || "all";

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [summary, setSummary] = useState(DEFAULT_SUMMARY);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);

  const [dateFilter, setDateFilter] = useState(
    searchParams.get("filter") || "all"
  );

  const [customRange, setCustomRange] = useState({
    startDate: searchParams.get("startDate") || "",
    endDate: searchParams.get("endDate") || "",
  });

  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "");

  const money = (value) => Number(value || 0).toFixed(2);

  const buildInvoiceUrl = ({
    nextStatus = selectedStatus,
    filter = dateFilter,
    range = customRange,
    page = pagination.page,
    limit = pagination.limit,
    search = searchTerm,
  }) => {
    const params = new URLSearchParams();

    params.set("filter", filter);
    params.set("page", String(page));
    params.set("limit", String(limit));

    if (search) {
      params.set("search", search);
    }

    if (filter === "custom") {
      if (range.startDate) {
        params.set("startDate", range.startDate);
      }

      if (range.endDate) {
        params.set("endDate", range.endDate);
      }
    }

    return `/invoices/${nextStatus}?${params.toString()}`;
  };

  const updateUrlParams = ({
    nextStatus = selectedStatus,
    filter = dateFilter,
    range = customRange,
    page = pagination.page,
    limit = pagination.limit,
    search = searchTerm,
  }) => {
    navigate(
      buildInvoiceUrl({
        nextStatus,
        filter,
        range,
        page,
        limit,
        search,
      }),
      { replace: false }
    );
  };

  const fetchInvoices = async ({
    invoiceStatus = "all",
    page = 1,
    limit = 25,
    filter = "all",
    range = { startDate: "", endDate: "" },
    search = "",
  }) => {
    setLoading(true);

    try {
      const user = JSON.parse(localStorage.getItem("user"));

      if (!user) {
        window.location.href = "/sign-in";
        return;
      }

      const params = new URLSearchParams();

      params.set("invoiceStatus", invoiceStatus);
      params.set("page", String(page));
      params.set("limit", String(limit));
      params.set("filter", filter);

      if (search) {
        params.set("search", search);
      }

      if (filter === "custom" && range.startDate && range.endDate) {
        params.set("startDate", range.startDate);
        params.set("endDate", range.endDate);
      }

      const response = await fetch(
        `${API_URL}/api/invoice/get/invoice?${params.toString()}`,
        {
          method: "GET",
          headers: {
            Accept: "*/*",
            "Content-Type": "application/json",
          },
        }
      );

      const result = await response.json();

      if (response.status === 200) {
        setData(result.invoices || []);
        setSummary(result.summary || DEFAULT_SUMMARY);
        setPagination(result.pagination || DEFAULT_PAGINATION);
      } else {
        setData([]);
        setSummary(DEFAULT_SUMMARY);
        setPagination(DEFAULT_PAGINATION);
      }
    } catch (error) {
      console.error("Error fetching invoices:", error);
      setData([]);
      setSummary(DEFAULT_SUMMARY);
      setPagination(DEFAULT_PAGINATION);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const filterFromUrl = searchParams.get("filter") || "all";
    const startDateFromUrl = searchParams.get("startDate") || "";
    const endDateFromUrl = searchParams.get("endDate") || "";
    const pageFromUrl = Number(searchParams.get("page")) || 1;
    const limitFromUrl = Number(searchParams.get("limit")) || 25;
    const searchFromUrl = searchParams.get("search") || "";

    const rangeFromUrl = {
      startDate: startDateFromUrl,
      endDate: endDateFromUrl,
    };

    setDateFilter(filterFromUrl);
    setCustomRange(rangeFromUrl);
    setSearchTerm(searchFromUrl);

    fetchInvoices({
      invoiceStatus: selectedStatus,
      page: pageFromUrl,
      limit: limitFromUrl,
      filter: filterFromUrl,
      range: rangeFromUrl,
      search: searchFromUrl,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStatus, searchParams.toString()]);

  const handleStatusCardClick = (nextStatus) => {
    updateUrlParams({
      nextStatus,
      filter: dateFilter,
      range: customRange,
      page: 1,
      limit: pagination.limit,
      search: searchTerm,
    });
  };

  const handleFilterChange = (newFilter) => {
    setDateFilter(newFilter);

    if (newFilter !== "custom") {
      updateUrlParams({
        nextStatus: selectedStatus,
        filter: newFilter,
        range: {
          startDate: "",
          endDate: "",
        },
        page: 1,
        limit: pagination.limit,
        search: searchTerm,
      });
    }
  };

  const handleCustomApply = () => {
    if (!customRange.startDate || !customRange.endDate) return;

    updateUrlParams({
      nextStatus: selectedStatus,
      filter: "custom",
      range: customRange,
      page: 1,
      limit: pagination.limit,
      search: searchTerm,
    });
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
  };

  const handleSearchSubmit = () => {
    updateUrlParams({
      nextStatus: selectedStatus,
      filter: dateFilter,
      range: customRange,
      page: 1,
      limit: pagination.limit,
      search: searchTerm,
    });
  };

  const handlePageChange = (newPage) => {
    updateUrlParams({
      nextStatus: selectedStatus,
      filter: dateFilter,
      range: customRange,
      page: newPage,
      limit: pagination.limit,
      search: searchTerm,
    });
  };

  const handleLimitChange = (newLimit) => {
    updateUrlParams({
      nextStatus: selectedStatus,
      filter: dateFilter,
      range: customRange,
      page: 1,
      limit: newLimit,
      search: searchTerm,
    });
  };

  const refreshCurrentPage = () => {
    fetchInvoices({
      invoiceStatus: selectedStatus,
      page: pagination.page,
      limit: pagination.limit,
      filter: dateFilter,
      range: customRange,
      search: searchTerm,
    });
  };

  return (
    <>
      <MasterLayout>
        <Breadcrumb title="Invoices" />

        <div className="mt-4 mb-20">
          <div className="col-xxl-12">
            <div className="mt-4">
            <div className="card p-3 border radius-8 mb-3">
              <DashboardFilter
                filter={dateFilter}
                onFilterChange={handleFilterChange}
                customRange={customRange}
                setCustomRange={setCustomRange}
                onCustomApply={handleCustomApply}
                loading={loading}
              />
            </div>
          </div>
            <div className="row gy-4">
              <div className="col-xxl-3 col-sm-6">
                <button
                  type="button"
                  className={`invoice-summary-card-btn ${
                    selectedStatus === "all" ? "active" : ""
                  }`}
                  onClick={() => handleStatusCardClick("all")}
                >
                  <div className="card p-3 shadow-2 radius-8 border input-form-light h-100 bg-gradient-end-1">
                    <div className="card-body p-0">
                      <div className="d-flex align-items-center gap-2">
                        <span className="mb-0 w-48-px h-48-px bg-primary-600 flex-shrink-0 text-white d-flex justify-content-center align-items-center rounded-circle h6 mb-0">
                          <Icon icon="mingcute:user-follow-fill" className="icon" />
                        </span>

                        <div className="text-start">
                          <span className="mb-2 fw-medium text-secondary-light text-sm">
                            Total Invoices
                          </span>
                          <br />
                          <span style={{ fontWeight: "bold" }}>
                            {summary.totalInvoicesCount}
                          </span>
                          <h6 className="fw-semibold">
                            £{money(summary.totalInvoiceAmountCount)}
                          </h6>
                        </div>
                      </div>
                    </div>
                  </div>
                </button>
              </div>

              <div className="col-xxl-3 col-sm-6">
                <button
                  type="button"
                  className={`invoice-summary-card-btn ${
                    selectedStatus === "paid" ? "active" : ""
                  }`}
                  onClick={() => handleStatusCardClick("paid")}
                >
                  <div className="card p-3 shadow-2 radius-8 border input-form-light h-100 bg-gradient-end-2">
                    <div className="card-body p-0">
                      <div className="d-flex align-items-center gap-2">
                        <span className="mb-0 w-48-px h-48-px bg-success-main flex-shrink-0 text-white d-flex justify-content-center align-items-center rounded-circle h6">
                          <Icon icon="mingcute:user-follow-fill" className="icon" />
                        </span>

                        <div className="text-start">
                          <span className="mb-2 fw-medium text-secondary-light text-sm">
                            Paid Invoices
                          </span>
                          <br />
                          <span style={{ fontWeight: "bold" }}>
                            {summary.paidInvoiceCount}
                          </span>
                          <h6 className="fw-semibold">
                            £{money(summary.paidInvoiceAmountCount)}
                          </h6>
                        </div>
                      </div>
                    </div>
                  </div>
                </button>
              </div>

              <div className="col-xxl-3 col-sm-6">
                <button
                  type="button"
                  className={`invoice-summary-card-btn ${
                    selectedStatus === "overdue" ? "active" : ""
                  }`}
                  onClick={() => handleStatusCardClick("overdue")}
                >
                  <div className="card p-3 shadow-2 radius-8 border input-form-light h-100 bg-gradient-end-4">
                    <div className="card-body p-0">
                      <div className="d-flex align-items-center gap-2">
                        <span className="mb-0 w-48-px h-48-px bg-danger-main flex-shrink-0 text-white d-flex justify-content-center align-items-center rounded-circle h6">
                          <Icon icon="mingcute:user-follow-fill" className="icon" />
                        </span>

                        <div className="text-start">
                          <span className="mb-2 fw-medium text-secondary-light text-sm">
                            Overdue Invoice (Unpaid)
                          </span>
                          <br />
                          <span style={{ fontWeight: "bold" }}>
                            {summary.overdueInvoiceCount}
                          </span>
                          <h6 className="fw-semibold">
                            £{money(summary.overdueInvoiceAmountCount)}
                          </h6>
                        </div>
                      </div>
                    </div>
                  </div>
                </button>
              </div>

              <div className="col-xxl-3 col-sm-6">
                <button
                  type="button"
                  className={`invoice-summary-card-btn ${
                    selectedStatus === "unpaid" ? "active" : ""
                  }`}
                  onClick={() => handleStatusCardClick("unpaid")}
                >
                  <div className="card p-3 shadow-2 radius-8 border input-form-light h-100 bg-gradient-end-4">
                    <div className="card-body p-0">
                      <div className="d-flex align-items-center gap-2">
                        <span className="mb-0 w-48-px h-48-px bg-danger-main flex-shrink-0 text-white d-flex justify-content-center align-items-center rounded-circle h6">
                          <Icon icon="mingcute:user-follow-fill" className="icon" />
                        </span>

                        <div className="text-start">
                          <span className="mb-2 fw-medium text-secondary-light text-sm">
                            Due Invoice (Unpaid)
                          </span>
                          <br />
                          <span style={{ fontWeight: "bold" }}>
                            {summary.unpaidInvoiceCount}
                          </span>
                          <h6 className="fw-semibold">
                            £{money(summary.unpaidInvoiceAmountCount)}
                          </h6>
                        </div>
                      </div>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>

          
        </div>

        <style>
          {`
            .invoice-summary-card-btn {
              width: 100%;
              border: 0;
              padding: 0;
              background: transparent;
              text-decoration: none;
              color: inherit;
              cursor: pointer;
              text-align: inherit;
            }

            .invoice-summary-card-btn .card {
              transition: all 0.18s ease;
            }

            .invoice-summary-card-btn:hover .card {
              transform: translateY(-2px);
              box-shadow: 0 14px 32px rgba(15, 23, 42, 0.12);
            }

            
          `}
        </style>

        {loading && (
          <div className="text-center mt-4">
            <Spinner />
          </div>
        )}

        {!loading && data.length === 0 && (
          <p className="text-center mt-4">No Invoices Found....</p>
        )}

        {!loading && data.length > 0 && (
          <InvoiceListLayer
            jobsData={data}
            pagination={pagination}
            onPageChange={handlePageChange}
            onLimitChange={handleLimitChange}
            loading={loading}
            searchTerm={searchTerm}
            onSearchTermChange={handleSearchChange}
            onSearchSubmit={handleSearchSubmit}
            onRefresh={refreshCurrentPage}
          />
        )}
      </MasterLayout>
    </>
  );
};

export default InvoiceListPage;